import React, { useEffect, useRef, useState } from 'react';
import ManageControls from 'src/components/manage-controls.jsx';
import { addClasses } from 'lib/helpers/class.js';
import { DetailButtons } from 'src/components/control-buttons.jsx';
import Panel from 'src/components/panel.jsx';
import Input from 'lib/components/input.jsx';
import Switch from 'lib/components/switch.jsx';
import Grid from 'lib/components/grid.jsx';
import { LoadingIndicator } from 'lib/components/loading.jsx';
import useManageCrud from 'src/@manage/use-manage.js';
import { useRouteParams } from 'src/hooks/use-route-params.js';
import { useForm } from 'lib/hooks/use-form.js';
import useS3Upload from 'src/hooks/use-upload.js';
import FileDnD from '#lib/components/file-dnd.jsx';
import Textarea from '#lib/components/textarea.jsx';
import { required, json } from '#lib/hooks/use-validator.js';

export default function ThemeEdit(routeProps) {
    const { id } = useRouteParams(routeProps);
    const [src, setSrc] = useState(null);

    const validation = {
        defaultStyles: [required, json]
    };

    const crud = useManageCrud(
        'theme',
        { id }
    );
    const uploadSource = useS3Upload();

    const form = useForm({
        validation,
        initialData: crud.data,
        errors: crud.error?.cause?.validationErrors,
        onSubmit: () => {
            const data = form.data;
            data.id = id;
            if (uploadSource.data) data.source = uploadSource.data.tempKey;
            crud.save.mutate(data);
        }
    });
    const { data, props, register, registerCheckbox, isSuccess, isLoading } = form;

    const onUpdateFiles = (_, files) => {
        const hasFile = Boolean(files?.[0]);
        if (hasFile) {
            setSrc(new File([], files[0].name));
            form.handleChange('source', files[0].name);
        }
    };

    const hasFile = Boolean(data.source);
    useEffect(() => {
        setSrc(hasFile ? new File([], data.source) : null);
    }, [hasFile]);

    return (
        <React.Fragment>
            <main className="p-4 pt-16">
                <header>
                    <p className="text-primary-600">{_t.crud.theme.theme()}</p>
                </header>
                <Panel as="form" className="p-1 pt-4 max-w-[1000px] overflow-hidden" {...props.form}>
                    {isLoading && (
                        <LoadingIndicator
                            className={addClasses(
                                'z-10 absolute top-0 left-0 w-full h-full justify-center items-center'
                            )}
                        />
                    )}
                    {isSuccess && (
                        <Grid>
                            <Grid.Slot span={12}>
                                <Switch.Full {...registerCheckbox('enabled')} label={_t.crud.theme.enabled()} />
                            </Grid.Slot>

                            <Grid.Slot span={6}>
                                <Input.Full {...register('name')} label={_t.crud.theme.name()} required />
                            </Grid.Slot>

                            <Grid.Slot span={6} />

                            <Grid.Slot span={6}>
                                <Textarea type="text">
                                    <Textarea.Label>{_t.crud.theme.defaultStyles()}</Textarea.Label>
                                    <Textarea.Area
                                        className={addClasses('rounded-md min-h-[40vh]')}
                                        {...register(`defaultStyles`)}
                                        required
                                        autoSize={false}
                                    />
                                </Textarea>
                            </Grid.Slot>

                            <Grid.Slot span={6}>
                                <FileDnD
                                    name="source"
                                    existingFiles={[src]}
                                    accept=".zip,application/zip,application/x-zip-compressed,application/x-zip,application/octet-stream"
                                    label={_t.crud.theme.source()}
                                    onUpdateFiles={onUpdateFiles}
                                    {...uploadSource.props}
                                />
                            </Grid.Slot>
                        </Grid>
                    )}
                    <ManageControls.ButtonSlot>
                        <DetailButtons
                            save={{
                                busy: crud.save.isLoading || uploadSource.uploadLoading,
                                ...props.submit
                            }}
                            add={true}
                            delete={{
                                onDelete: crud.delete.mutate,
                                busy: crud.delete.isLoading
                            }}
                            changeTracker={{ rowKey: id }}
                        />
                    </ManageControls.ButtonSlot>
                </Panel>
            </main>
        </React.Fragment>
    );
}
